import Slider from "react-slick";
import { useEffect, useState, useRef, Children, cloneElement } from 'react';
import ConditionalWrapper from '@Components/Utilities/ConditionalWrapper';
import { gtmEventType } from '@Components/Utilities/SandhillsConstants';
import { gtmPushEvent } from '@Components/Utilities/SandhillsUtility';
import ListingImage from '@Components/Listings/ListingImage';
import Link from '@Components/Shared/Controls/Link';
import FleetEvaluatorValues from '@Components/Shared/FleetEvaluatorValues';
import WatchlistExpansion from '@Components/Shared/Listings/WatchlistsExpansion';
import SandhillsImage from "./Controls/SandhillsImage";
import TranslationText from '@Components/Utilities/TranslationText';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import translator from '@Components/Utilities/Translator';
import { useWindowSize } from '@Components/Utilities/WindowSize';
import atoms from '@Components/Utilities/SandhillsAtoms';
import { useRecoilValue } from 'recoil';

const SandhillsSlickSlider = (props) => {
    const [activeSlide, setActiveSlide] = useState(0);
    const sliderRef = useRef(null);
    const windowSize = useWindowSize();
    const [slideCount, setSlideCount] = useState(0);
    const showSectionTitle = props?.showSectionTitle ?? true;
    const sectionRef = useRef(null);
    const [loadAllImages, setLoadAllImages] = useState(false);

    useEffect(() => {
        const slickContainer = getSlickContainer();
        if (slickContainer) {
            const clonedSlides = getSlickTrackSlides(slickContainer, '.slick-slide.slick-cloned');
            setSlideTabIndexes(clonedSlides, 'a', '-1');
        }
    }, []);

    useEffect(() => {
        if (props.ChildComponent !== 'FeaturedEventsPanel') return;
        const sliderElement = sliderRef.current.innerSlider.list;
        if (sliderElement) {
            const slides = sliderElement.querySelectorAll(".slick-slide, .slick-slide.slick-cloned");
            slides.forEach(slide => slide.classList.remove(`slide-count-${slideCount}`));
            slides.forEach(slide => slide.classList.add(`slide-count-${slides.length}`));
            setSlideCount(slides.length);
        }
    }, [windowSize.width]);

    useEffect(() => {
        const slickContainer = getSlickContainer();

        if (slickContainer) {
            const inactiveSlides = getSlickTrackSlides(slickContainer, '.slick-slide:not(.slick-cloned):not(.slick-active)');
            const activeSlides = getSlickTrackSlides(slickContainer, '.slick-slide.slick-active');

            setSlideTabIndexes(inactiveSlides, 'a', '-1');
            setSlideTabIndexes(activeSlides, 'a', '0');
        }
    }, [activeSlide]);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (props.HasSplitHomePageDrilldownCatTabs) {
                sliderRef.current.slickGoTo(props.SlideIndex);
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [props.SlideIndex]);

    useEffect(() => {
        // Solves issue where lazy load images don't load until carousel scrolls.
        const node = sectionRef.current;
        if (!node || !window.IntersectionObserver) return;

        const observer = new window.IntersectionObserver(
            (entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        if (!loadAllImages) {
                            setLoadAllImages(true);
                        }
                    }
                });
            },
            { threshold: 0 }
        );
        observer.observe(node);

        return () => observer.disconnect();
    }, []);

    const setSlideTabIndexes = (slides, element, tabIndex) => {
        slides.forEach(function (el) {
            const anchorElements = el.getElementsByTagName(element);

            Array.from(anchorElements).forEach(function (el) {
                el.setAttribute('tabindex', tabIndex);
            });
        });
    };

    const getSlickTrackSlides = (slickContainer, selectors) => {
        return slickContainer.querySelectorAll(selectors);
    };

    const getSlickContainer = () => {
        return document.getElementsByClassName(`${props.OuterDivClass}`)[0];
    };

    const defaultSettings = {
        dots: true,
        infinite: false,
        speed: 500,
        className: props.SliderClass,
        slidesToShow: 6,
        slidesToScroll: 6,
        autoplaySpeed: 6000,
        responsive: [
            {
                breakpoint: 1430,
                settings: {
                    slidesToShow: 6,
                    slidesToScroll: 6,
                }
            },
        ]
    };

    const sliderSettings = {
        ...defaultSettings, ...props.settings,
        ...(props.ChildComponent === 'FeaturedEventsPanel' && {
            beforeChange: (current, next) => {
                const slides = sliderRef.current.innerSlider.list.querySelectorAll(".slick-slide, .slick-slide.slick-cloned");
                slides.forEach(slide => slide.classList.add(`slide-count-${slides.length}`));

            },
            afterChange: (current) => {
                const slides = sliderRef.current.innerSlider.list.querySelectorAll(".slick-slide, .slick-slide.slick-cloned");
                slides.forEach(slide => slide.classList.add(`slide-count-${slides.length}`));

            }
        })
    };

    const checkSlideSettings = (settings, numSlides) => {
        if (settings.slidesToShow > numSlides) {
            settings.slidesToShow = numSlides;
        }
        if (settings.slidesToScroll > numSlides) {
            settings.slidesToScroll = numSlides;
        }

        if (settings.responsive && settings.responsive.length > 0) {
            for (var i = 0; settings.responsive.length > i; i++) {
                checkSlideSettings(settings.responsive[i].settings, numSlides);
            }
        }
    }
    checkSlideSettings(sliderSettings, props.children.length);

    const handleViewAllClick = () => {
        if (props.ChildComponent == 'RecommendedListingsPanel') {
            gtmPushEvent(gtmEventType.recommendedListingsAll);
        }
    };


    return (
        <ConditionalWrapper condition={props.ShowSection} wrapper={children =>
            <section className={props.SectionClass} ref={sectionRef}>
                {showSectionTitle &&
                    <div className='section-title'>
                        <h2>
                            <span className='section-title-text' dangerouslySetInnerHTML={{ __html: props.Title }}></span>
                        </h2>
                        {props.LogoURL &&
                            <span>
                                <br />
                                <LazyLoadImage src={props.LogoURL} alt="" className='wordmark' />
                            </span>}
                        {props.ViewAllLink &&
                            <Link Url={props.ViewAllLink} ClassName='view-all-link' text={props.ViewAllText} hiddenText={props.Title} onClick={handleViewAllClick} />}
                    </div>
                }
                <div className={props.OuterDivClass}>
                    {children}
                </div>
            </section>
        }>
        <Slider {...sliderSettings} ref={sliderRef} beforeChange={(_, next) => setActiveSlide(next)}>
            {Children.map(props.children, child =>
                child ? cloneElement(child, { loadAllImages }) : child
            )}
        </Slider>
        </ConditionalWrapper>
    );
};

const Panel = (props) => {
    const [displayWatchlistExpansion, setDisplayWatchlistExpansion] = useState(false);
    const [unsaveWatchlist, setUnsaveWatchlist] = useState(false);
    const linkTarget = props.OpenInNewTab ? '_blank' : '_self';
    const routes = useRecoilValue(atoms.routes);
    const currentSite = useRecoilValue(atoms.currentWebsite);
    const efColor = '#ffcc01';
    const lsmColor = '#c65431';
    const emColor = '#725146';
    let auctionIconColor = '';
    if (currentSite?.Site?.IsLivestockMarket) {
        auctionIconColor = lsmColor;
    } else if (currentSite?.Site?.IsEquineMarket) {
        auctionIconColor = emColor;
    } else {
        auctionIconColor = efColor;
    }

    const toggleWatchlistExpansion = (count) => {
        setUnsaveWatchlist(false);
        if (displayWatchlistExpansion) {
            props.getUserWatchlist();
        }
        if (count == 0) {
            props.setUserWatchlist(props.UserWatchlist.filter((c) => props.IsWatchList ? c.Id != props.Id : c.ListingID != props.Id));
            setUnsaveWatchlist(true);
        }
        setDisplayWatchlistExpansion(!displayWatchlistExpansion);
    }

    const getReccomendedListingHeightClass = () => {
        if (props.WholeSalePrice && props.ShowEvaluatorValues && (props.FEAuctionValue || props.FERetailValue)) {
            return ' fe-wholesale-height';
        } else if (props.WholeSalePrice) {
            return ' wholesale-height';
        } else if (!props.ShowEvaluatorValues || (props.ShowEvaluatorValues && !(props.FEAuctionValue || props.FERetailValue))) {
            return ' no-evaluator';
        }
        return '';
    };

    return (
        <>
            {props.PanelType == 'RegionedListPanel' &&
                <a href={props.Url} className={props.PanelClass} style={props.style}>
                    <div className='listing-content'>
                        <div className='top-region'>
                            <SandhillsImage
                                divClass='image-wrapper'
                                src={props.ImageValues.original}
                                alt={props.ImageValues.alt}
                                className={props.ImageValues.className}
                                height={props.ImageValues.height}
                                width={props.ImageValues.width}
                            />
                            {(props.Category && props.ListingName) &&
                                <div className='name-and-category'>
                                    <span className='listing-name'><span className='ub-num'>{props.ListingYear + ' '}</span>{props.ListingName}</span>
                                    <span className={props.CategoryClass}>{props.Category}</span>
                                </div>}
                        </div>
                        <div>
                            {props.Price &&
                                <div>
                                    {props.PriceLabel && <span className='listing-price-label'>{props.PriceLabel}:&nbsp;</span>}
                                    <span className={props.PriceClass}>{props.Price}</span>
                                </div>
                            }
                            {props.ButtonText &&
                                <span className={'view-listing-details-link des-view-listingDetails'}><strong>{props.ButtonText}</strong></span>}
                        </div>
                    </div>
                </a>
            }
            {props.PanelType == 'RecommendedListingsPanel' &&
                <div className={props.PanelClass + getReccomendedListingHeightClass()} style={props.style}>
                    <div className='listing-content'>
                        <div className='top-region'>
                            <ListingImage
                                {...props.ListingImageModel}
                                DetailUrl={props.Url}
                                IsAT={props.IsATAuction}
                                IsEF={props.IsEFAuction}
                                IsCarouselRecommendedListing={true}
                                LinkTarget={linkTarget}
                                ListingId={props.ListingId}
                                toggleWatchlistExpansion={toggleWatchlistExpansion}
                                unsaveWatchlist={unsaveWatchlist}
                                UserWatchlist={props.UserWatchlist}
                                ShowAuctionBranding={true}
                                ListingTitle={props.ListingName}
                            />
                            {displayWatchlistExpansion &&
                                <WatchlistExpansion
                                    {...props}
                                    CustomWatchlists={props.CustomWatchlists}
                                    DomNode={"listing-carousel"}
                                    Id={props.ListingId}
                                    toggleWatchlistExpansion={toggleWatchlistExpansion}
                                    usePortal={true}
                                />
                            }
                        </div>
                        {!currentSite?.Site?.IsAircraft ?
                            <div className='bottom-region'>
                                {(props.ListingName) &&
                                    <a href={props.Url} className='name' target={linkTarget} onClick={() => gtmPushEvent(gtmEventType.recommendedListingClick)}
                                        aria-label={linkTarget == '_blank' ? (props.ListingYear + " " + props.ListingName) + " (" + translator.translate('sandhills.opensinanewtab') + ")" : (props.ListingYear + " " + props.ListingName)}>
                                        <strong><span className='ub-num'>{props.ListingYear + ' '}</span>{props.ListingName}</strong>
                                    </a>}

                                <strong className='name'>{((props.IsAT || props.IsEF) && props.PriceLabel) &&
                                    <>{props.PriceLabel}: </>}{(props.Price != 'Auction') && <span className={(props.IsAT || props.IsEF) ? 'red' : ''}>{props.Price}</span>}
                                </strong>
                                {props.WholeSalePrice &&
                                    <strong className='name wholesale'><TranslationText tKey='sandhills.wholesaleprice' />: {props.WholeSalePrice}</strong>
                                }
                                {(props.ShowEvaluatorValues) && (props.FERetailValue || props.FEAuctionValue) &&
                                    <FleetEvaluatorValues
                                        FEAuctionValue={props.FEAuctionValue}
                                        FERetailValue={props.FERetailValue}
                                        FEImagePath={props.FEImagePath}
                                        ShowErrorMessage={true}
                                    />}
                            </div> :
                            <div className='bottom-region'>
                                <span className='title'>
                                    {props.RegNumber}
                                </span>
                                {(props.ListingName) &&
                                    <a href={props.Url} className='name' target={linkTarget} onClick={() => gtmPushEvent(gtmEventType.recommendedListingClick)}
                                        aria-label={linkTarget == '_blank' ? (props.ListingYear + " " + props.ListingName) + " (" + translator.translate('sandhills.opensinanewtab') + ")" : (props.ListingYear + " " + props.ListingName)}>
                                        <span className='ub-num'>{props.ListingYear + ' '}</span>{props.ListingName}
                                    </a>}


                            </div>
                        }
                    </div>
                </div>
            }
            {props.PanelType == 'FeaturedEventsPanel' &&
                <a className={props.PanelClass} href={props.DealerUrl} >
                    <div class="featured-img">
                        <img src={props.LogoURL} alt={props.AuctioneerName} />
                    </div>
                    <div class="featured-details">
                        <strong class="featured-event">{props.EventName}</strong><br></br>
                        <strong class="featured-info">{props.EventDescription}</strong>
                    </div>
                </a>
            }
            {props.PanelType == 'AuctionsForYouPanel' &&
                <div className="recommended-auction">
                    <a className={props.PanelClass} href={props.DealerUrl} >
                        {props.IsEFAuction &&
                            <div className="auction-icon" style={{ backgroundColor: auctionIconColor }} >
                                {currentSite?.Site?.IsLivestockMarket || currentSite?.Site?.IsEquineMarket ?
                                    <img src={routes.CDNBaseUrl + 'Images/Icons/Miscellaneous/production-sale.png'} alt="Production Sale Logo" />
                                    : <img src={routes.CDNBaseUrl + 'Images/Icons/Miscellaneous/ef-icon-black.png'} alt="Equipment Facts Logo" />
                                }
                            </div>}
                        {props.IsATAuction &&
                            <div className="auction-icon at">
                                <img src={routes.CDNBaseUrl + 'Images/Icons/Miscellaneous/at-icon-white.png'} alt="Auction Time Logo" ></img>
                            </div>}
                        <div className="recommended-auction-img">
                            <img src={props.LogoURL} alt={props.AuctioneerName} />
                        </div>
                        <div className="recommended-auction-details">
                            <strong className="event-name">{props.EventName}</strong>
                            <br></br>
                            <span className="event-info">{props.LotString}</span><br></br>

                            <span className="event-info">
                                {props.AuctionEndDateDescription &&
                                    <>
                                        {translator.translate('sandhills.auctionends')}
                                        <span className="event-info red"> {props.AuctionEndDateDescription}</span>
                                    </>}
                            </span><br></br>
                            <span className="event-info">{props.LocationString}</span><br></br>
                        </div>
                    </a>
                </div>
            }
            {props.PanelType == 'FTIWTB' &&
                <>
                    {props.IsLastItem ?
                        <div className={props.PanelClass + ' last-item'}>
                            <a className='cloud-link' href={routes.CloudWTBs} target='_blank'>
                                <div>
                                    <img src='/Content/images/FTIPlug/logo-fti-dark.png' alt='Fast Track Iron Logo' className='fti-logo' />
                                </div>
                                <div>
                                    {translator.translate('sandhills.tradepubwebsite.mvc.viewall')} <i className='fas fa-external-link-alt' />
                                </div>
                                <span className='visually-hidden'>Opens in a new tab</span>
                            </a>
                        </div>
                        :
                        <div className={props.PanelClass}>
                            <span className='title'>{translator.translate('sandhills.tradepubwebsite.mvc.wanttobuy')}</span>
                            <div className='fti-wtb-bg'>
                                <div className='make-model'>{props.Title}</div>
                                <div className='category'>{props.Category}</div>
                                <br />
                                <div className='created-on'><strong>{translator.translate('sandhills.createdon')}:</strong> {props.CreatedOn}</div>
                                <div className='location'><strong>{translator.translate('sandhills.location')}:</strong> {props.State}</div>
                            </div>
                            <div className='contact-info'>
                                <div className='phone'><i className='fas fa-phone-alt' /><a className='phone-link' href={'tel:' + props.UnformattedPhone} link='tel'><strong>&nbsp;{props.FormattedPhone}</strong></a></div>
                                <div className='name'>{props.Name}</div>
                            </div>
                        </div>
                    }
                </>
            }
            {
                props.PanelType !== 'RegionedListPanel' && props.PanelType !== 'RecommendedListingsPanel' && props.PanelType !== 'FeaturedEventsPanel' && props.PanelType !== 'AuctionsForYouPanel' &&
                <a href={props.Url} className={props.PanelClass} id={props.id} target={props.OpenInNewTab ? '_blank' : '_self'} aria-label={props.OpenInNewTab ? props.Text || props.SiteName + " (" + translator.translate('sandhills.opensinanewtab') + ")" : props.Text}>
                    <div className={props.OuterDivClass}>
                        {props.ImgUrl ? (
                            <>
                                <div className='image-wrapper'>
                                        <LazyLoadImage key={props.loadAllImages ? `eager-${props.ImgUrl}` : `lazy-${props.ImgUrl}`} src={props.ImgUrl} alt={props.AltText ? props.AltText : ''} title={props.Title ? props.Title : ''} className={props.ImgClass} fetchpriority='high' visibleByDefault={props.loadAllImages} />
                                    {props.Caption &&
                                        <div className='caption' id='caption'>
                                            <div className='captionTransition'><strong>{props.Caption}</strong> &nbsp;<i className='fas fa-chevron-circle-right' aria-hidden='true' />
                                            </div>
                                        </div>}
                                </div>
                                {props.Text &&
                                    <h3>
                                        <div className='view-listing-details-link des-view-listingDetails'>
                                            <span dangerouslySetInnerHTML={{ __html: props.Text }}></span>
                                        </div>
                                    </h3>}
                            </>
                        ) : (
                            <h3>
                                <div className='panel-text-wrapper'>
                                    {props.Text &&
                                        <span dangerouslySetInnerHTML={{ __html: props.Text }}></span>}
                                </div>
                            </h3>
                        )}
                    </div>
                    {props.OpenInNewTab && <span className='visually-hidden'>({translator.translate('sandhills.opensinanewtab')})</span>}
                </a>
            }
        </>
    );
};

export { Panel, SandhillsSlickSlider };